import axios from 'axios'
import Auth from './auth'

export const siteUrl = import.meta.env.REACT_APP_SITE_URL
export const apiUrl = import.meta.env.REACT_APP_API_URL

/*
  Sem timeout, uma conexão pendurada deixa a tela girando para sempre — a promise nunca
  resolve e o spinner nunca sai. 30 s é folgado para conexão ruim e ainda assim finito.

  Upload e download não cabem aqui: logomarca, anexo de cliente e PDF de relatório
  levam mais. Esses sítios passam UPLOAD_TIMEOUT explicitamente.
*/
export const REQUEST_TIMEOUT = 30000
export const UPLOAD_TIMEOUT = 120000

const cutileiApi = axios.create ({
  baseURL: apiUrl,
  timeout: REQUEST_TIMEOUT
})

const leHeader = (headers, nome) => (
  typeof headers?.get === 'function' ? headers.get (nome) : headers?.[nome]
)

const escreveHeader = (headers, nome, valor) => {
  if (typeof headers?.set === 'function') headers.set (nome, valor)
  else headers[nome] = valor
}

/*
  Injeta o Authorization em vez de exigir que cada chamada monte o seu. Eram 207 sítios
  chamando `Auth.getRequestAuth ()` à mão. Aqui não é preciso ponte nenhuma: o token
  mora no `localStorage` e a leitura é síncrona.

  `Auth.getRequestAuth ()` continua existindo e funcionando — passa a ser redundante,
  não errado —, e é o que permite fazer a troca sem tocar nos 207 call sites.

  Duas ressalvas, nesta ordem:

  1. Não sobrescreve header que a chamada já trouxe. Quem passa o seu tem motivo.
  2. `anonymous: true` desliga a injeção, e neste repositório isso **não é teórico**.
     O link público de agendamento roda na mesma origem do painel, então um dono de
     salão logado que abra o link tem token no `localStorage`. As quatro rotas que o
     fluxo usa são `silentAuth` na API: `customers.store`, `verify_availability` e
     `customer_schedules.store/update`. Elas hoje recebem a chamada anônima, e com
     token o `Professional.actedByClient` inverte — muda o texto da notificação — e a
     auditoria passa a registrar o salão como autor de um agendamento que o cliente
     fez. Por isso o fluxo público declara `anonymous` e continua anônimo.
*/
cutileiApi.interceptors.request.use (config => {
  if (config.anonymous) return config

  const token = Auth.getToken ()

  if (!token) return config
  if (leHeader (config.headers, 'Authorization')) return config

  escreveHeader (config.headers, 'Authorization', `Bearer ${token}`)

  return config
})

cutileiApi.interceptors.response.use (
  response => response,
  async error => {
    /*
      403 não entra aqui de propósito: é permissão revogada no servidor, não sessão
      inválida, então derrubar a sessão seria errado. O que faltava era ele parar de
      ser silencioso, e isso está em `apiError.js`, que é quem decide o que vira evento.
    */
    if (error.response?.status === 401 && Auth.getUserType () === 'business') {
      Auth.businessSignOut ()
      window.location.reload ()
    }

    return Promise.reject (error)
  }
)

export default cutileiApi
